import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { can } from '@/lib/permissions'
import {
  Shield,
  Users,
  User,
  HardDrive,
  Trophy,
  Palette,
  Calendar,
  Activity,
  Database,
  Server,
  Trash2,
  Upload,
  Filter,
  AlertCircle,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  TrendingUp,
  BarChart3,
  MessageSquare,
  Vote,
  Eye,
  Download,
  RotateCcw,
  Music,
  ExternalLink,
  UserCog,
  Plus,
  Edit2,
  ToggleLeft,
  ToggleRight,
  Boxes,
  Mic,
  Zap,
  Globe,
  Wifi,
  WifiOff,
  Copy,
  Check,
  RefreshCw,
  Save,
  Cpu,
  PowerOff,
  ShoppingBag,
  Lock,
  Search,
  X as XIcon,
} from 'lucide-react'
import { io } from 'socket.io-client'
import { authAPI, adminAPI, filesAPI, eventsAPI, tournamentsAPI, gameServersAPI, spotifyAPI, helpdeskGroupsAPI, modulesAPI, serviceConfigAPI, pterodactylAPI, settingsAPI, clanAPI, pointsAPI, badgeAPI, reportsAPI } from '@/lib/api'
import { applyTheme } from '../App'
import PageHeader from '../components/PageHeader'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import ModerationPanel from '@/components/ModerationPanel'
import KassaSyncButton from '@/components/kassa/KassaSyncButton'
import KassaAdminStatus from '@/components/kassa/KassaAdminStatus'
import KassaUserPushCard from '@/components/kassa/KassaUserPushCard'
import ClanTab from './admin/ClanTab'
import FailedLoginsCard from '@/components/FailedLoginsCard'
import { SecurityTabContent, KassaSyncTabContent } from '@/components/admin/SetupPanels'

// ── Clan Kaarten Tab ──────────────────────────────────────────────────────────
export default function AdminPage() {
  // Tab uit de URL, zodat de sidebar rechtstreeks naar /admin?tab=badges kan linken.
  // De tabs zijn gegroepeerd in twee "momenten":
  //   Live  — dingen die je tijdens de LAN aanraakt (users, moderation, groups, servers)
  //   Setup — dingen die je vóór/tussen LAN's configureert (modules, services, system, clan, music, badges, files)
  const [searchParams, setSearchParams] = useSearchParams()
  const TAB_SECTION = {
    users: 'live', moderation: 'live', groups: 'live', servers: 'live', feedback: 'live',
    modules: 'setup', services: 'setup', system: 'setup', clan: 'setup',
    music: 'setup', badges: 'setup', files: 'setup',
    security: 'setup', kassa: 'setup',
  }
  const urlTab = searchParams.get('tab')
  const adminTab = urlTab && TAB_SECTION[urlTab] ? urlTab : 'users'
  // Als er een onbekende ?tab= in de URL zit: rustig terug naar users
  // schrijven zodat een refresh geen "verloren"-tab meer laat zien.
  useEffect(() => {
    if (urlTab && !TAB_SECTION[urlTab]) {
      const q = new URLSearchParams(searchParams)
      q.set('tab', 'users')
      q.set('section', 'live')
      setSearchParams(q, { replace: true })
    }
  }, [urlTab])
  const section = searchParams.get('section') || TAB_SECTION[adminTab] || 'live'

  // Tabs die in beide secties zichtbaar zijn — voor die tabs mogen we de
  // huidige section behouden ipv naar de "vaste" section springen.
  const AMBI_TABS = new Set(['clan'])
  const kiesTab = (t) => {
    const q = new URLSearchParams(searchParams)
    q.set('tab', t)
    if (TAB_SECTION[t] && !AMBI_TABS.has(t)) q.set('section', TAB_SECTION[t])
    setSearchParams(q, { replace: true })
  }
  const kiesSection = (s) => {
    const q = new URLSearchParams(searchParams)
    q.set('section', s)
    // spring meteen naar de eerste tab van die sectie
    const eerste = s === 'live' ? 'users' : 'modules'
    q.set('tab', eerste)
    setSearchParams(q, { replace: true })
  }

  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)

  // Dashboard stats
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFiles: 0,
    storageUsed: 0,
    nasUsed: 0,
    nasTotal: 0,
    nasFree: 0,
    totalTournaments: 0,
    totalEvents: 0,
  })

  // Enhanced stats

  // Files management
  const [files, setFiles] = useState([])
  const [filesLoading, setFilesLoading] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [uploadData, setUploadData] = useState({
    file: null,
    category: 'Other',
    description: '',
  })
  const [uploading, setUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  // Recent activity
  const [recentActivity, setRecentActivity] = useState([])

  // System status
  const [systemStatus, setSystemStatus] = useState({
    database: 'checking',
    backend: 'checking',
    storage: 'checking',
  })

  // Users management
  const [users, setUsers] = useState([])
  // Klik-om-te-sorteren state voor de gebruikerstabel. Default = username asc.
  const [userSort, setUserSort] = useState({ by: 'username', dir: 'asc' })
  const toggleUserSort = (key) => setUserSort(s =>
    s.by === key ? { by: key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { by: key, dir: 'asc' })
  // Vrije zoekterm die op username / display_name / email tegelijk filtert.
  const [userSearch, setUserSearch] = useState('')
  const [usersLoading, setUsersLoading] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [userEditData, setUserEditData] = useState({
    role: '',
    email: '',
    displayName: '',
    username: '',
  })
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createUserData, setCreateUserData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    displayName: '',
  })
  const [createError, setCreateError] = useState('')

  // Pterodactyl storage
  const [ptStorage, setPtStorage] = useState(null)
  const [ptStorageLoading, setPtStorageLoading] = useState(false)

  // Theme
  const [activeTheme, setActiveTheme] = useState('neon-blue')
  const [savingTheme, setSavingTheme] = useState(false)

  // Game servers management
  const [gameServers, setGameServers] = useState([])
  const [serversLoading, setServersLoading] = useState(false)
  const [editingServer, setEditingServer] = useState(null)
  const [serverDialogOpen, setServerDialogOpen] = useState(false)
  const [serverFormData, setServerFormData] = useState({
    name: '',
    game_type: '',
    description: '',
    ip_address: '',
    port: '',
    max_players: '',
    current_players: '',
    status: 'online',
    join_url: '',
    version: '',
    mods: '',
    map_name: '',
    password_protected: false,
    is_active: true,
    rcon_password: '',
    overlay_identifier: '',
  })
  const [serverError, setServerError] = useState('')

  // Spotify settings
  const [spotifySettings, setSpotifySettings] = useState(null)
  const [spotifyLoading, setSpotifyLoading] = useState(false)
  const [spotifyPlaylistId, setSpotifyPlaylistId] = useState('')
  const [spotifyError, setSpotifyError] = useState('')
  const [connectingSpotify, setConnectingSpotify] = useState(false)
  const [spotifyAuthUrl, setSpotifyAuthUrl] = useState('')
  const [spotifyCallbackUrl, setSpotifyCallbackUrl] = useState('')
  const [showManualAuth, setShowManualAuth] = useState(false)

  // Reset stats state
  const [resettingStats, setResettingStats] = useState(false)
  const [resetStatsDialogOpen, setResetStatsDialogOpen] = useState(false)

  // Delete all tournaments state
  const [deletingTournaments, setDeletingTournaments] = useState(false)
  const [deleteTournamentsDialogOpen, setDeleteTournamentsDialogOpen] = useState(false)

  // System shutdown state
  const [shutdownDialogOpen, setShutdownDialogOpen] = useState(false)
  const [shutdownProgress, setShutdownProgress] = useState([])
  const [shuttingDown, setShuttingDown] = useState(false)

  // Helpdesk groups management
  const [groups, setGroups] = useState([])
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
  })
  const [groupError, setGroupError] = useState('')
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState('')

  // Modules management
  const [modules, setModules] = useState([])
  const [modulesLoading, setModulesLoading] = useState(false)

  // Service config management
  const [serviceOverview, setServiceOverview] = useState(null)
  const [serviceConfigs, setServiceConfigs] = useState([])
  const [serviceLoading, setServiceLoading] = useState(false)
  const [serviceEditValues, setServiceEditValues] = useState({})
  const [serviceSaving, setServiceSaving] = useState({})
  const [serviceChecking, setServiceChecking] = useState({})
  const [serviceStatus, setServiceStatus] = useState({})
  const [copiedIp, setCopiedIp] = useState(null)

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Just now'

    const date = new Date(dateString)
    const now = new Date()
    const seconds = Math.floor((now - date) / 1000)

    if (seconds < 60) return 'Just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
    return date.toLocaleDateString()
  }

  // Check admin access on mount
  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        setLoading(true)
        const token = localStorage.getItem('auth_token')

        if (!token) {
          navigate('/')
          return
        }

        const userResponse = await authAPI.getCurrentUser()
        const user = userResponse.data.user

        if (!can(user.role, 'manage_users')) {
          console.log('Access denied: User is not admin or superadmin')
          navigate('/')
          return
        }

        setCurrentUser(user)
        await fetchDashboardData()
      } catch (err) {
        console.error('Failed to verify admin access:', err)
        navigate('/')
      } finally {
        setLoading(false)
      }
    }

    checkAdminAccess()
  }, [navigate])

  // Fetch Spotify settings on mount
  useEffect(() => {
    if (can(currentUser?.role, 'manage_spotify')) {
      fetchSpotifySettings()
    }
  }, [currentUser])

  // Fetch helpdesk groups on mount
  useEffect(() => {
    if (can(currentUser?.role, 'helpdesk_access')) {
      fetchGroups()
    }
  }, [currentUser])

  // Fetch modules on mount
  useEffect(() => {
    if (can(currentUser?.role, 'view_modules')) {
      fetchModules()
    }
  }, [currentUser])

  // Fetch current theme on mount
  useEffect(() => {
    settingsAPI.getTheme().then(r => setActiveTheme(r.data.theme)).catch(() => {})
  }, [])

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    try {
      setError(null)

      // Fetch data in parallel
      const [statsResponse, filesResponse, eventsResponse, tournamentsResponse] =
        await Promise.allSettled([
          adminAPI.getSystemStats(),
          filesAPI.getFiles(),
          eventsAPI.getAllPages(),
          tournamentsAPI.getTournaments(),
        ])

      // Process stats
      if (statsResponse.status === 'fulfilled') {
        const statsData = statsResponse.value.data
        setStats({
          totalUsers: statsData.totalUsers || 0,
          totalFiles: statsData.totalFiles || 0,
          storageUsed: statsData.storageUsed || 0,
          nasUsed: statsData.nasUsed || 0,
          nasTotal: statsData.nasTotal || 0,
          nasFree: statsData.nasFree || 0,
          totalTournaments: statsData.totalTournaments || 0,
          totalEvents: statsData.totalEvents || 0,
        })

        // Update system status
        setSystemStatus({
          database: 'online',
          backend: 'online',
          storage: 'online',
        })
      } else {
        // Fallback to counting from API responses
        const filesData = filesResponse.status === 'fulfilled' ? filesResponse.value.data.files || [] : []
        const eventsData = eventsResponse.status === 'fulfilled' ? eventsResponse.value.data.pages || [] : []
        const tournamentsData = tournamentsResponse.status === 'fulfilled' ? tournamentsResponse.value.data.tournaments || [] : []

        const totalSize = filesData.reduce((sum, file) => sum + (file.size || 0), 0)

        setStats({
          totalUsers: 0,
          totalFiles: filesData.length,
          storageUsed: totalSize,
          totalTournaments: tournamentsData.length,
          totalEvents: eventsData.length,
        })

        setSystemStatus({
          database: 'online',
          backend: 'online',
          storage: 'online',
        })
      }

      // Process files
      if (filesResponse.status === 'fulfilled') {
        const filesData = filesResponse.value.data.files || []
        setFiles(filesData)

        // Generate recent activity from files
        const recentFileActivity = filesData.slice(0, 5).map(file => ({
          type: 'file_upload',
          description: `File uploaded: ${file.filename}`,
          timestamp: file.created_at,
          user: file.uploaded_by || 'Unknown',
        }))
        setRecentActivity(recentFileActivity)
      }

    } catch (err) {
      console.error('Failed to fetch dashboard data:', err)
      setError('Failed to load some dashboard data')
      setSystemStatus({
        database: 'error',
        backend: 'error',
        storage: 'error',
      })
    }
  }

  // Fetch enhanced statistics

  // Fetch activity log

  // Fetch files
  const fetchFiles = async () => {
    try {
      setFilesLoading(true)
      const response = await filesAPI.getFiles()
      setFiles(response.data.files || [])
    } catch (err) {
      console.error('Failed to fetch files:', err)
    } finally {
      setFilesLoading(false)
    }
  }

  // Handle file upload
  const handleFileUpload = async (e) => {
    e.preventDefault()

    if (!uploadData.file) {
      setUploadError('Please select a file')
      return
    }

    try {
      setUploading(true)
      setUploadError(null)
      setUploadSuccess(false)

      const formData = new FormData()
      formData.append('file', uploadData.file)
      formData.append('category', uploadData.category)
      formData.append('description', uploadData.description)

      await filesAPI.uploadFile(formData)

      setUploadSuccess(true)
      setUploadData({
        file: null,
        category: 'Other',
        description: '',
      })

      // Reset file input
      const fileInput = document.getElementById('file-upload-input')
      if (fileInput) fileInput.value = ''

      // Refresh files list
      await fetchFiles()
      await fetchDashboardData()

      // Close dialog after 2 seconds
      setTimeout(() => {
        setUploadDialogOpen(false)
        setUploadSuccess(false)
      }, 2000)
    } catch (err) {
      console.error('Upload failed:', err)
      setUploadError(err.response?.data?.error || 'Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  // Handle file delete
  const handleDeleteFile = async (fileId, filename) => {
    if (!confirm(`Are you sure you want to delete "${filename}"?`)) {
      return
    }

    try {
      await filesAPI.deleteFile(fileId)
      await fetchFiles()
      await fetchDashboardData()
    } catch (err) {
      console.error('Failed to delete file:', err)
      alert('Failed to delete file. Please try again.')
    }
  }

  // Fetch users
  const fetchUsers = async () => {
    try {
      setUsersLoading(true)
      const response = await adminAPI.getUsers()
      setUsers(response.data.users || [])
    } catch (err) {
      console.error('Failed to fetch users:', err)
    } finally {
      setUsersLoading(false)
    }
  }

  // Bij mount de gebruikerslijst ophalen — anders staat "0 gebruikers"
  // tot Ververs geklikt wordt.
  useEffect(() => { fetchUsers() }, [])

  // Handle edit user
  const handleEditUser = (user) => {
    setEditingUser(user)
    setUserEditData({
      role: user.role,
      email: user.email,
      displayName: user.display_name || '',
      username: user.username || '',
    })
    setEditDialogOpen(true)
  }

  // Handle update user
  const handleUpdateUser = async (e) => {
    e.preventDefault()

    try {
      await adminAPI.updateUser(editingUser.id, {
        role: userEditData.role,
        email: userEditData.email,
        displayName: userEditData.displayName,
        username: userEditData.username,
      })

      setEditDialogOpen(false)
      setEditingUser(null)
      await fetchUsers()
      await fetchDashboardData()
    } catch (err) {
      console.error('Failed to update user:', err)
      alert(err.response?.data?.error || 'Failed to update user. Please try again.')
    }
  }

  // Handle password reset — geeft admin een klaar-te-communiceren nieuw
  // wachtwoord (auto-generated of ingegeven).
  const handleResetPassword = async (userId, username) => {
    const custom = window.prompt(
      `Nieuw wachtwoord voor "${username}"?\n\nLaat leeg voor automatisch gegenereerd (12 tekens).`
    )
    // null = cancel — niks doen. Lege string = auto-generate.
    if (custom === null) return
    if (custom && custom.length > 0 && custom.length < 8) {
      alert('Wachtwoord moet minstens 8 tekens zijn.')
      return
    }
    try {
      const r = await adminAPI.resetUserPassword(userId, custom || null)
      window.prompt(
        `Wachtwoord gereset voor ${r.data.username}.\n\nKopieer dit — het wordt nergens opgeslagen:`,
        r.data.password
      )
    } catch (err) {
      alert(err.response?.data?.error || 'Wachtwoord resetten mislukt.')
    }
  }

  // Handle delete user
  const handleDeleteUser = async (userId, username) => {
    if (!confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return
    }

    try {
      await adminAPI.deleteUser(userId)
      await fetchUsers()
      await fetchDashboardData()
    } catch (err) {
      console.error('Failed to delete user:', err)
      alert(err.response?.data?.error || 'Failed to delete user. Please try again.')
    }
  }

  // Handle reset user stats
  const handleResetUserStats = async (userId, username) => {
    if (!confirm(`Are you sure you want to reset all stats for user "${username}"?\n\nThis will permanently delete:\n• All player statistics (wins, losses, points, streaks)\n• All earned achievements\n• All tournament placements\n\nThis action cannot be undone!`)) {
      return
    }

    try {
      const response = await adminAPI.resetUserStats(userId)
      alert(response.data.message || `Stats for "${username}" have been reset successfully!`)
    } catch (err) {
      console.error('Failed to reset user stats:', err)
      alert(err.response?.data?.error || 'Failed to reset user stats. Please try again.')
    }
  }

  // Handle create user
  const handleCreateUser = async (e) => {
    e.preventDefault()
    setCreateError('')

    // Validation
    if (createUserData.username.length < 3) {
      setCreateError('Username must be at least 3 characters')
      return
    }

    if (createUserData.password.length < 8) {
      setCreateError('Password must be at least 8 characters')
      return
    }

    if (createUserData.password !== createUserData.confirmPassword) {
      setCreateError('Passwords do not match')
      return
    }

    try {
      await adminAPI.createUser({
        username: createUserData.username,
        email: createUserData.email,
        password: createUserData.password,
        role: createUserData.role,
        displayName: createUserData.displayName,
      })

      setCreateDialogOpen(false)
      setCreateUserData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'user',
        displayName: '',
      })
      setCreateError('')
      await fetchUsers()
      await fetchDashboardData()
    } catch (err) {
      console.error('Failed to create user:', err)
      setCreateError(err.response?.data?.error || 'Failed to create user. Please try again.')
    }
  }

  // Fetch game servers
  const fetchGameServers = async () => {
    try {
      setServersLoading(true)
      const response = await gameServersAPI.getAllServers()
      setGameServers(response.data.servers || [])
    } catch (err) {
      console.error('Failed to fetch game servers:', err)
    } finally {
      setServersLoading(false)
    }
  }

  const fetchPtStorage = async () => {
    try {
      setPtStorageLoading(true)
      const res = await pterodactylAPI.getStorageUsage()
      setPtStorage(res.data)
    } catch (err) {
      console.error('Failed to fetch pterodactyl storage:', err)
    } finally {
      setPtStorageLoading(false)
    }
  }

  // Handle edit server
  const handleEditServer = (server) => {
    setEditingServer(server)
    setServerFormData({
      name: server.name || '',
      game_type: server.game_type || '',
      description: server.description || '',
      ip_address: server.ip_address || '',
      port: server.port || '',
      max_players: server.max_players || '',
      current_players: server.current_players || '',
      status: server.status || 'online',
      join_url: server.join_url || '',
      version: server.version || '',
      mods: server.mods || '',
      map_name: server.map_name || '',
      password_protected: server.password_protected || false,
      is_active: server.is_active !== undefined ? server.is_active : true,
      rcon_password: server.rcon_password || '',
      overlay_identifier: server.overlay_identifier || '',
    })
    setServerDialogOpen(true)
  }

  // Handle create/update server
  const handleSaveServer = async (e) => {
    e.preventDefault()
    setServerError('')

    // Validation
    if (!serverFormData.name || !serverFormData.game_type) {
      setServerError('Name and game type are required')
      return
    }

    try {
      const data = {
        ...serverFormData,
        port: serverFormData.port ? parseInt(serverFormData.port) : null,
        max_players: serverFormData.max_players ? parseInt(serverFormData.max_players) : null,
        current_players: serverFormData.current_players ? parseInt(serverFormData.current_players) : 0,
        password_protected: serverFormData.password_protected ? 1 : 0,
        is_active: serverFormData.is_active ? 1 : 0,
      }

      if (editingServer) {
        await gameServersAPI.updateServer(editingServer.id, data)
      } else {
        await gameServersAPI.createServer(data)
      }

      setServerDialogOpen(false)
      setEditingServer(null)
      setServerFormData({
        name: '',
        game_type: '',
        description: '',
        ip_address: '',
        port: '',
        max_players: '',
        current_players: '',
        status: 'online',
        join_url: '',
        version: '',
        mods: '',
        map_name: '',
        password_protected: false,
        is_active: true,
      })
      setServerError('')
      await fetchGameServers()
    } catch (err) {
      console.error('Failed to save game server:', err)
      setServerError(err.response?.data?.error || 'Failed to save game server. Please try again.')
    }
  }

  // Handle delete server
  const handleDeleteServer = async (serverId, serverName) => {
    if (!confirm(`Server "${serverName}" verwijderen?`)) {
      return
    }

    try {
      await gameServersAPI.deleteServer(serverId)
      await fetchGameServers()
    } catch (err) {
      console.error('Failed to delete server:', err)
      alert(err.response?.data?.error || 'Verwijderen mislukt.')
    }
  }

  // Direct-toggle voor deelnemer-zichtbaarheid — één klik verschuift de
  // is_active-flag. Frontend /game-servers filtert al op is_active, dus
  // dit bepaalt of deelnemers de server zien.
  const handleToggleServerVisibility = async (server) => {
    try {
      await gameServersAPI.updateServer(server.id, { ...server, is_active: server.is_active ? 0 : 1 })
      await fetchGameServers()
    } catch (err) {
      console.error('Failed to toggle visibility:', err)
      alert(err.response?.data?.error || 'Zichtbaarheid wijzigen mislukt.')
    }
  }

  // Fetch Spotify settings
  const fetchSpotifySettings = async () => {
    try {
      setSpotifyLoading(true)
      setSpotifyError('')
      const response = await spotifyAPI.getSettings()
      setSpotifySettings(response.data)
      if (response.data.playlistId) {
        setSpotifyPlaylistId(response.data.playlistId)
      }
    } catch (err) {
      console.error('Failed to fetch Spotify settings:', err)
      setSpotifyError('Failed to load Spotify settings')
    } finally {
      setSpotifyLoading(false)
    }
  }

  // Get Spotify authorization URL (manual auth step 1)
  const handleGetSpotifyAuthUrl = async () => {
    try {
      setSpotifyError('')
      const response = await spotifyAPI.getAuthUrl()
      setSpotifyAuthUrl(response.data.url)
      setShowManualAuth(true)
    } catch (err) {
      console.error('Failed to get Spotify auth URL:', err)
      setSpotifyError(err.response?.data?.error || 'Failed to get authorization URL')
    }
  }

  // Complete manual Spotify authentication (step 2)
  const handleManualSpotifyAuth = async () => {
    try {
      if (!spotifyCallbackUrl.trim()) {
        setSpotifyError('Please paste the callback URL')
        return
      }

      setConnectingSpotify(true)
      setSpotifyError('')

      await spotifyAPI.manualAuth(spotifyCallbackUrl.trim())

      // Success! Refresh settings
      await fetchSpotifySettings()
      setShowManualAuth(false)
      setSpotifyAuthUrl('')
      setSpotifyCallbackUrl('')
      setConnectingSpotify(false)
    } catch (err) {
      console.error('Manual Spotify auth failed:', err)
      setSpotifyError(err.response?.data?.error || err.response?.data?.details?.error_description || 'Failed to authenticate with Spotify')
      setConnectingSpotify(false)
    }
  }

  // Disconnect Spotify
  const handleDisconnectSpotify = async () => {
    if (!confirm('Are you sure you want to disconnect Spotify? This will remove all settings.')) {
      return
    }
    try {
      setSpotifyLoading(true)
      setSpotifyError('')
      await spotifyAPI.disconnect()
      setSpotifySettings(null)
      setSpotifyPlaylistId('')
    } catch (err) {
      console.error('Failed to disconnect Spotify:', err)
      setSpotifyError(err.response?.data?.error || 'Failed to disconnect Spotify')
    } finally {
      setSpotifyLoading(false)
    }
  }

  // Update playlist configuration
  const handleUpdatePlaylist = async (e) => {
    e.preventDefault()
    if (!spotifyPlaylistId || !spotifyPlaylistId.trim()) {
      setSpotifyError('Please enter a playlist ID')
      return
    }
    try {
      setSpotifyLoading(true)
      setSpotifyError('')
      const response = await spotifyAPI.updatePlaylistConfig(spotifyPlaylistId.trim())
      setSpotifySettings({
        ...spotifySettings,
        playlistId: spotifyPlaylistId.trim(),
        playlistName: response.data.playlistName
      })
      alert('Playlist configured successfully!')
    } catch (err) {
      console.error('Failed to update playlist:', err)
      setSpotifyError(err.response?.data?.error || 'Failed to update playlist. Make sure the playlist ID is correct and you have access to it.')
    } finally {
      setSpotifyLoading(false)
    }
  }

  // Handle reset player stats
  const handleResetPlayerStats = async () => {
    try {
      setResettingStats(true)
      await adminAPI.resetPlayerStats()
      setResetStatsDialogOpen(false)
      alert('Player stats have been reset successfully. All players will start fresh!')
    } catch (err) {
      console.error('Failed to reset player stats:', err)
      alert(err.response?.data?.error || 'Failed to reset player stats. Please try again.')
    } finally {
      setResettingStats(false)
    }
  }

  // Handle delete all tournaments
  const handleDeleteAllTournaments = async () => {
    try {
      setDeletingTournaments(true)
      const response = await adminAPI.deleteAllTournaments()
      setDeleteTournamentsDialogOpen(false)
      alert(response.data.message || 'All tournaments have been deleted successfully!')
    } catch (err) {
      console.error('Failed to delete all tournaments:', err)
      alert(err.response?.data?.error || 'Failed to delete all tournaments. Please try again.')
    } finally {
      setDeletingTournaments(false)
    }
  }

  const handleShutdown = async () => {
    const token = localStorage.getItem('token')
    const socket = io({ auth: { token }, transports: ['websocket'] })

    setShutdownProgress([])
    setShuttingDown(true)

    socket.on('system-shutdown-progress', ({ step, message, status }) => {
      setShutdownProgress(prev => [...prev, { step, message, status, time: new Date().toLocaleTimeString() }])
    })

    socket.on('disconnect', () => {
      setShutdownProgress(prev => [...prev, { step: 99, message: 'Verbinding verbroken — server is offline', status: 'done', time: new Date().toLocaleTimeString() }])
    })

    try {
      await adminAPI.shutdownSystem()
    } catch (err) {
      setShutdownProgress(prev => [...prev, { step: -1, message: 'Fout: ' + (err.response?.data?.message || err.message), status: 'error', time: new Date().toLocaleTimeString() }])
      setShuttingDown(false)
    }
  }

  // Fetch helpdesk groups
  const fetchGroups = async () => {
    try {
      setGroupsLoading(true)
      const response = await helpdeskGroupsAPI.getGroups()
      setGroups(response.data.groups || [])
    } catch (err) {
      console.error('Failed to fetch groups:', err)
      setGroupError(err.response?.data?.error || 'Failed to fetch groups')
    } finally {
      setGroupsLoading(false)
    }
  }

  // Fetch all modules
  const fetchModules = async () => {
    try {
      setModulesLoading(true)
      const response = await modulesAPI.getAll()
      setModules(response.data || [])
    } catch (err) {
      console.error('Failed to fetch modules:', err)
    } finally {
      setModulesLoading(false)
    }
  }

  // Toggle module enabled/disabled
  const handleToggleModule = async (moduleKey) => {
    try {
      await modulesAPI.toggle(moduleKey)
      await fetchModules()
    } catch (err) {
      console.error('Failed to toggle module:', err)
    }
  }

  // Fetch service config overview
  const fetchServiceOverview = async () => {
    try {
      setServiceLoading(true)
      const response = await serviceConfigAPI.getOverview()
      setServiceOverview(response.data)
      setServiceConfigs(response.data.service_configs || [])
      const initValues = {}
      ;(response.data.service_configs || []).forEach(c => { initValues[c.key] = c.value })
      setServiceEditValues(initValues)
    } catch (err) {
      console.error('Failed to fetch service overview:', err)
    } finally {
      setServiceLoading(false)
    }
  }

  const handleSaveServiceConfig = async (key) => {
    const value = serviceEditValues[key]
    if (!value?.trim()) return
    try {
      setServiceSaving(prev => ({ ...prev, [key]: true }))
      await serviceConfigAPI.update(key, value.trim())
      setServiceConfigs(prev => prev.map(c => c.key === key ? { ...c, value: value.trim() } : c))
    } catch (err) {
      alert(err.response?.data?.error || 'Opslaan mislukt')
    } finally {
      setServiceSaving(prev => ({ ...prev, [key]: false }))
    }
  }

  const handleCheckService = async (key) => {
    try {
      setServiceChecking(prev => ({ ...prev, [key]: true }))
      setServiceStatus(prev => ({ ...prev, [key]: null }))
      const response = await serviceConfigAPI.check(key)
      setServiceStatus(prev => ({ ...prev, [key]: response.data }))
    } catch (err) {
      setServiceStatus(prev => ({ ...prev, [key]: { online: false, ms: 0 } }))
    } finally {
      setServiceChecking(prev => ({ ...prev, [key]: false }))
    }
  }

  const handleCopyIp = (ip) => {
    navigator.clipboard.writeText(ip).then(() => {
      setCopiedIp(ip)
      setTimeout(() => setCopiedIp(null), 2000)
    })
  }

  // Create or update group
  const handleSaveGroup = async (e) => {
    e.preventDefault()
    try {
      setGroupError('')
      if (selectedGroup) {
        // Update existing group
        await helpdeskGroupsAPI.updateGroup(selectedGroup.id, groupFormData)
      } else {
        // Create new group
        await helpdeskGroupsAPI.createGroup(groupFormData)
      }
      setGroupDialogOpen(false)
      setSelectedGroup(null)
      setGroupFormData({ name: '', description: '', color: '#3b82f6' })
      await fetchGroups()
    } catch (err) {
      console.error('Failed to save group:', err)
      setGroupError(err.response?.data?.error || 'Failed to save group')
    }
  }

  // Delete group
  const handleDeleteGroup = async (groupId) => {
    if (!confirm('Are you sure you want to delete this group? This action cannot be undone.')) {
      return
    }
    try {
      await helpdeskGroupsAPI.deleteGroup(groupId)
      await fetchGroups()
    } catch (err) {
      console.error('Failed to delete group:', err)
      alert(err.response?.data?.error || 'Failed to delete group')
    }
  }

  // Open edit dialog
  const handleEditGroup = (group) => {
    setSelectedGroup(group)
    setGroupFormData({
      name: group.name,
      description: group.description || '',
      color: group.color || '#3b82f6',
    })
    setGroupDialogOpen(true)
  }

  // Open create dialog
  const handleCreateGroup = () => {
    setSelectedGroup(null)
    setGroupFormData({ name: '', description: '', color: '#3b82f6' })
    setGroupDialogOpen(true)
  }

  // Add member to group
  const handleAddMember = async () => {
    try {
      if (!selectedUserId) {
        alert('Please select a user')
        return
      }
      await helpdeskGroupsAPI.addMember(selectedGroup.id, selectedUserId)
      setAddMemberDialogOpen(false)
      setSelectedUserId('')
      await fetchGroups()
      // Refresh group details if viewing
      if (selectedGroup) {
        const response = await helpdeskGroupsAPI.getGroup(selectedGroup.id)
        setSelectedGroup(response.data.group)
      }
    } catch (err) {
      console.error('Failed to add member:', err)
      alert(err.response?.data?.error || 'Failed to add member')
    }
  }

  // Remove member from group
  const handleRemoveMember = async (groupId, userId) => {
    if (!confirm('Are you sure you want to remove this member from the group?')) {
      return
    }
    try {
      await helpdeskGroupsAPI.removeMember(groupId, userId)
      await fetchGroups()
      // Refresh group details if viewing
      if (selectedGroup && selectedGroup.id === groupId) {
        const response = await helpdeskGroupsAPI.getGroup(groupId)
        setSelectedGroup(response.data.group)
      }
    } catch (err) {
      console.error('Failed to remove member:', err)
      alert(err.response?.data?.error || 'Failed to remove member')
    }
  }

  // Filter files by category
  const filteredFiles = categoryFilter === 'all'
    ? files
    : files.filter(file => file.category === categoryFilter)

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-6 w-6 animate-spin text-red-500" />
            <span className="text-lg text-muted-foreground">Verifying admin access...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Shield}
        accent="slate"
        title="Beheer"
        subtitle="Instellingen, gebruikers, modules"
      />

      {/* Error alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tabs for different sections */}
      <Tabs value={adminTab} onValueChange={kiesTab} className="space-y-6">
        {/* Primaire tabstrip — "wanneer ben ik hier?" */}
        <div className="flex gap-2 border-b border-border mb-3">
          {[
            { key: 'live',  label: 'Live',  hint: 'Tijdens de LAN' },
            { key: 'setup', label: 'Setup', hint: 'Vóór/tussen LAN\'s' },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => kiesSection(s.key)}
              className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors ${
                section === s.key
                  ? 'border-acid text-acid'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.label}
              <span className="ml-2 font-normal text-xs text-muted-foreground">{s.hint}</span>
            </button>
          ))}
          {section === 'live' && (
            <div className="ml-auto text-xs text-muted-foreground self-center">
              Live-monitoring en snelle acties zitten op <a href="/regie" className="text-acid hover:underline">/regie</a>
            </div>
          )}
        </div>

        {/* Secundaire tabstrip — inhoud per sectie */}
        <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1">
          {section === 'live' ? (
            <>
              <TabsTrigger value="users">Gebruikers</TabsTrigger>
              <TabsTrigger value="moderation">Moderatie</TabsTrigger>
              <TabsTrigger value="groups">Groepen</TabsTrigger>
              <TabsTrigger value="feedback">Feedback</TabsTrigger>
              <TabsTrigger value="servers" onClick={fetchPtStorage}>Servers</TabsTrigger>
              <TabsTrigger value="clan">Kaarten</TabsTrigger>
            </>
          ) : (
            <>
              <TabsTrigger value="modules">Modules</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="kassa">Kassa-sync</TabsTrigger>
              <TabsTrigger value="system">System</TabsTrigger>
              <TabsTrigger value="clan">Kaarten</TabsTrigger>
              <TabsTrigger value="music">Music</TabsTrigger>
              <TabsTrigger value="badges">Badges</TabsTrigger>
              <TabsTrigger value="files">Files</TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Security-tab — was vroeger dashboard-tab; verhuisd naar Setup
            omdat je deze toggles nooit tijdens de LAN wijzigt. */}
        <TabsContent value="security" className="space-y-6">
          <SecurityTabContent />
        </TabsContent>

        {/* Nieuws-broadcast is verhuisd naar /events (sidebar-item Nieuws /
            notifications) — één plek voor eenmalige toasts én aankondigingen
            die op de home blijven staan. */}

        {/* Feedback-tab — bug/idee-bus */}
        <TabsContent value="feedback" className="space-y-6">
          <FeedbackAdminTab />
        </TabsContent>

        {/* Kassa-sync tab — ClubKassa-koppeling: admin status + sync + user push.
            Ook Setup want een sync draai je niet tijdens de LAN. */}
        <TabsContent value="kassa" className="space-y-6">
          <KassaSyncTabContent />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Gebruikersbeheer</h2>
              <p className="text-muted-foreground">Accounts en rollen beheren</p>
            </div>
            <div className="flex items-center space-x-2">
              <Button onClick={fetchUsers} variant="outline">
                <Activity className="h-4 w-4 mr-2" />
                Ververs
              </Button>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Users className="h-4 w-4 mr-2" />
                Nieuwe gebruiker
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle>Alle gebruikers</CardTitle>
                  <CardDescription>
                    {users.length} gebruiker{users.length !== 1 ? 's' : ''} geregistreerd
                    {userSearch.trim() && ` · gefilterd op "${userSearch.trim()}"`}
                  </CardDescription>
                </div>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                         placeholder="Zoek op naam, weergave, e-mail…"
                         className="pl-8 pr-8" />
                  {userSearch && (
                    <button onClick={() => setUserSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            title="Wissen">
                      <XIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-red-500" />
                </div>
              ) : users.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      {[
                        { key: 'username',     label: 'Gebruikersnaam' },
                        { key: 'display_name', label: 'Weergavenaam' },
                        { key: 'email',        label: 'E-mail' },
                        { key: 'role',         label: 'Rol' },
                        { key: 'created_at',   label: 'Aangemeld' },
                      ].map(col => (
                        <TableHead key={col.key}
                                   className="cursor-pointer select-none hover:text-foreground"
                                   onClick={() => toggleUserSort(col.key)}>
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {userSort.by === col.key && (
                              <span className="text-acid">{userSort.dir === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </span>
                        </TableHead>
                      ))}
                      <TableHead className="text-right">Acties</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...users].filter(u => {
                      const q = userSearch.trim().toLowerCase()
                      if (!q) return true
                      return [u.username, u.display_name, u.email]
                        .filter(Boolean)
                        .some(v => String(v).toLowerCase().includes(q))
                    }).sort((a, b) => {
                      const av = a[userSort.by] ?? ''
                      const bv = b[userSort.by] ?? ''
                      // created_at → tijd, rest → string. Alles case-insensitive.
                      let cmp
                      if (userSort.by === 'created_at') {
                        cmp = new Date(av).getTime() - new Date(bv).getTime()
                      } else {
                        cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase(), 'nl')
                      }
                      return userSort.dir === 'asc' ? cmp : -cmp
                    }).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>{user.username}</span>
                            {user.id === currentUser?.id && (
                              <Badge variant="outline" className="text-xs">Jij</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.display_name || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.email}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              user.role === 'superadmin' ? 'border-yellow-400/70 text-yellow-400 bg-yellow-400/10' :
                              user.role === 'admin' ? 'border-red-500/50 text-red-500' :
                              user.role === 'referee' ? 'border-yellow-500/50 text-yellow-500' :
                              'border-blue-500/50 text-blue-500'
                            }
                          >
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatRelativeTime(user.created_at)}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                            onClick={() => handleEditUser(user)}
                          >
                            Bewerken
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-orange-500 hover:text-orange-600 hover:bg-orange-500/10"
                            onClick={() => handleResetUserStats(user.id, user.username)}
                            title="Reset user stats"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-acid hover:text-acid/80 hover:bg-acid/10"
                            onClick={() => handleResetPassword(user.id, user.username)}
                            title="Wachtwoord resetten"
                          >
                            <Lock className="h-4 w-4" />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                              onClick={() => handleDeleteUser(user.id, user.username)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">Geen gebruikers gevonden</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit User Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
                <DialogDescription>
                  Update user role, email, or display name for {editingUser?.username}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user-username">Gebruikersnaam</Label>
                  <Input
                    id="user-username"
                    type="text"
                    value={userEditData.username}
                    onChange={(e) => setUserEditData({ ...userEditData, username: e.target.value })}
                    placeholder="gebruikersnaam"
                  />
                  {editingUser && userEditData.username !== editingUser.username && (
                    <p className="text-xs text-signal">
                      ⚠ Bij een wijziging moet {editingUser.display_name || editingUser.username} opnieuw inloggen — het huidige JWT-token bevat de oude naam.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Enkel letters, cijfers en <code>. _ -</code>. Minstens 3 tekens.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="user-role">Role</Label>
                  <Select
                    value={userEditData.role}
                    onValueChange={(value) => setUserEditData({ ...userEditData, role: value })}
                  >
                    <SelectTrigger id="user-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="referee">Referee</SelectItem>
                      <SelectItem value="helpdesk">Helpdesk</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      {currentUser?.role === 'superadmin' && (
                        <SelectItem value="superadmin">Superadmin</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="user-email">Email</Label>
                  <Input
                    id="user-email"
                    type="email"
                    value={userEditData.email}
                    onChange={(e) => setUserEditData({ ...userEditData, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="user-display-name">Display Name</Label>
                  <Input
                    id="user-display-name"
                    type="text"
                    value={userEditData.displayName}
                    onChange={(e) => setUserEditData({ ...userEditData, displayName: e.target.value })}
                    placeholder="Optional display name"
                  />
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Save Changes
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Create User Dialog */}
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Add a new user account to the system
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleCreateUser} className="space-y-4">
                {createError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{createError}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="create-username">Username *</Label>
                  <Input
                    id="create-username"
                    type="text"
                    value={createUserData.username}
                    onChange={(e) => setCreateUserData({ ...createUserData, username: e.target.value })}
                    placeholder="username (min 3 characters)"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-email">Email *</Label>
                  <Input
                    id="create-email"
                    type="email"
                    value={createUserData.email}
                    onChange={(e) => setCreateUserData({ ...createUserData, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-password">Password *</Label>
                  <Input
                    id="create-password"
                    type="password"
                    value={createUserData.password}
                    onChange={(e) => setCreateUserData({ ...createUserData, password: e.target.value })}
                    placeholder="Min 8 characters"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-confirm-password">Confirm Password *</Label>
                  <Input
                    id="create-confirm-password"
                    type="password"
                    value={createUserData.confirmPassword}
                    onChange={(e) => setCreateUserData({ ...createUserData, confirmPassword: e.target.value })}
                    placeholder="Confirm password"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-role">Role *</Label>
                  <Select
                    value={createUserData.role}
                    onValueChange={(value) => setCreateUserData({ ...createUserData, role: value })}
                  >
                    <SelectTrigger id="create-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="referee">Referee</SelectItem>
                      <SelectItem value="helpdesk">Helpdesk</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      {currentUser?.role === 'superadmin' && (
                        <SelectItem value="superadmin">Superadmin</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-display-name">Display Name (Optional)</Label>
                  <Input
                    id="create-display-name"
                    type="text"
                    value={createUserData.displayName}
                    onChange={(e) => setCreateUserData({ ...createUserData, displayName: e.target.value })}
                    placeholder="Optional display name"
                  />
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCreateDialogOpen(false)
                      setCreateError('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Create User
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Games">Games</SelectItem>
                  <SelectItem value="Maps">Maps</SelectItem>
                  <SelectItem value="Mods">Mods</SelectItem>
                  <SelectItem value="Tools">Tools</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-500 hover:bg-red-600 text-white">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Upload New File</DialogTitle>
                  <DialogDescription>
                    Add a new file to the shared library
                  </DialogDescription>
                </DialogHeader>

                {uploadSuccess ? (
                  <div className="py-8">
                    <div className="flex flex-col items-center space-y-4">
                      <CheckCircle className="h-16 w-16 text-green-500" />
                      <p className="text-lg font-medium">Upload Successful!</p>
                      <p className="text-sm text-muted-foreground">File has been added to the library</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleFileUpload} className="space-y-4">
                    {uploadError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{uploadError}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="file-upload-input">File</Label>
                      <Input
                        id="file-upload-input"
                        type="file"
                        onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                        disabled={uploading}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={uploadData.category}
                        onValueChange={(value) => setUploadData({ ...uploadData, category: value })}
                        disabled={uploading}
                      >
                        <SelectTrigger id="category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Games">Games</SelectItem>
                          <SelectItem value="Maps">Maps</SelectItem>
                          <SelectItem value="Mods">Mods</SelectItem>
                          <SelectItem value="Tools">Tools</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description (Optional)</Label>
                      <Textarea
                        id="description"
                        placeholder="Enter file description..."
                        value={uploadData.description}
                        onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                        disabled={uploading}
                        rows={3}
                      />
                    </div>

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setUploadDialogOpen(false)}
                        disabled={uploading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-red-500 hover:bg-red-600"
                        disabled={uploading}
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-orange-500/30">
            <CardHeader>
              <CardTitle>File Management</CardTitle>
              <CardDescription>
                {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                </div>
              ) : filteredFiles.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filename</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead>Uploaded By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFiles.map((file) => (
                      <TableRow key={file.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span>{file.filename}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{file.category || 'Other'}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatFileSize(file.file_size)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatRelativeTime(file.created_at)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {file.uploaded_by_name || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => handleDeleteFile(file.id, file.filename)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <HardDrive className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">No files found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Game Servers Tab */}
        <TabsContent value="servers" className="space-y-6">
          {/* Pterodactyl servers info card */}
          <Card className="border-neon-blue/30 bg-gradient-to-br from-card to-neon-blue/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-neon-blue" />
                  <CardTitle className="text-neon-blue">Pterodactyl Servers</CardTitle>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-neon-blue/50 text-neon-blue hover:bg-neon-blue/10"
                  onClick={() => navigate('/game-servers')}
                >
                  <Server className="h-4 w-4 mr-1.5" />
                  Beheer via Game Servers pagina
                </Button>
              </div>
              <CardDescription>
                MOHAA, Quake 3 Arena, TetriNET en andere Pterodactyl servers worden beheerd via de Game Servers pagina (start/stop/restart beschikbaar voor admins).
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Pterodactyl Resources — één card per server, 3 lijnen */}
          <PterodactylResourcesCard ptStorage={ptStorage} loading={ptStorageLoading} onRefresh={fetchPtStorage} />


          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-blue-500">Handmatige Servers</h2>
              <p className="text-muted-foreground">Servers die niet via Pterodactyl draaien</p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={fetchGameServers}
                variant="outline"
                className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
              >
                <Activity className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={() => {
                  setEditingServer(null)
                  setServerFormData({
                    name: '',
                    game_type: '',
                    description: '',
                    ip_address: '',
                    port: '',
                    max_players: '',
                    current_players: '',
                    status: 'online',
                    join_url: '',
                    version: '',
                    mods: '',
                    map_name: '',
                    password_protected: false,
                    is_active: true,
                  })
                  setServerDialogOpen(true)
                }}
                className="bg-acid hover:bg-acid/90 text-black"
              >
                <Server className="h-4 w-4 mr-2" />
                Server toevoegen
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!confirm('Alle Pterodactyl-servers importeren als game-servers-rijen? Bestaande servers worden overgeslagen. Nieuwe servers komen verborgen — jij bepaalt daarna wie zichtbaar is.')) return
                  try {
                    const r = await gameServersAPI.importFromPterodactyl()
                    alert(`Geïmporteerd: ${r.data.imported} · Overgeslagen: ${r.data.skipped}`)
                    await fetchGameServers()
                  } catch (e) {
                    alert('Import mislukt: ' + (e.response?.data?.error || e.message))
                  }
                }}>
                <Server className="h-4 w-4 mr-2" />
                Importeer uit Pterodactyl
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Alle game-servers</CardTitle>
              <CardDescription>
                {gameServers.length} server{gameServers.length !== 1 ? 's' : ''} geconfigureerd · klik op "Zichtbaar/Verborgen" om per server te schakelen
              </CardDescription>
            </CardHeader>
            <CardContent>
              {serversLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
              ) : gameServers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Game Type</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Players</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gameServers.map((server) => (
                      <TableRow key={server.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center space-x-2">
                            <Server className="h-4 w-4 text-muted-foreground" />
                            <span>{server.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{server.game_type}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {server.ip_address ? `${server.ip_address}${server.port ? `:${server.port}` : ''}` : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {server.max_players ? `${server.current_players || 0}/${server.max_players}` : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              server.status === 'online' ? 'border-green-500/50 text-green-500' :
                              server.status === 'maintenance' ? 'border-yellow-500/50 text-yellow-500' :
                              'border-red-500/50 text-red-500'
                            }
                          >
                            {server.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <button type="button"
                                  onClick={() => handleToggleServerVisibility(server)}
                                  title={server.is_active ? 'Klik om te verbergen' : 'Klik om zichtbaar te maken'}
                                  className={`px-2 py-1 rounded text-xs font-mono uppercase tracking-widest border transition-colors ${
                                    server.is_active
                                      ? 'border-live bg-live/10 text-live hover:bg-live/20'
                                      : 'border-border bg-muted/40 text-muted-foreground hover:border-live/40'
                                  }`}>
                            {server.is_active ? 'Zichtbaar' : 'Verborgen'}
                          </button>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                            onClick={() => handleEditServer(server)}
                          >
                            Bewerken
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => handleDeleteServer(server.id, server.name)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <Server className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">No game servers configured</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create/Edit Server Dialog */}
          <Dialog open={serverDialogOpen} onOpenChange={setServerDialogOpen}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingServer ? 'Edit Game Server' : 'Add New Game Server'}</DialogTitle>
                <DialogDescription>
                  {editingServer ? 'Update game server information' : 'Add a new game server to the list'}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSaveServer} className="space-y-4">
                {serverError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{serverError}</AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="server-name">Server Name *</Label>
                    <Input
                      id="server-name"
                      type="text"
                      value={serverFormData.name}
                      onChange={(e) => setServerFormData({ ...serverFormData, name: e.target.value })}
                      placeholder="My Awesome Server"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="server-game-type">Game Type *</Label>
                    <Input
                      id="server-game-type"
                      type="text"
                      value={serverFormData.game_type}
                      onChange={(e) => setServerFormData({ ...serverFormData, game_type: e.target.value })}
                      placeholder="Minecraft, CS:GO, etc."
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="server-description">Description</Label>
                  <Textarea
                    id="server-description"
                    value={serverFormData.description}
                    onChange={(e) => setServerFormData({ ...serverFormData, description: e.target.value })}
                    placeholder="Server description..."
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="server-ip">IP Address</Label>
                    <Input
                      id="server-ip"
                      type="text"
                      value={serverFormData.ip_address}
                      onChange={(e) => setServerFormData({ ...serverFormData, ip_address: e.target.value })}
                      placeholder="192.168.1.100"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="server-port">Port</Label>
                    <Input
                      id="server-port"
                      type="number"
                      value={serverFormData.port}
                      onChange={(e) => setServerFormData({ ...serverFormData, port: e.target.value })}
                      placeholder="25565"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="server-join-url">Join URL</Label>
                  <Input
                    id="server-join-url"
                    type="text"
                    value={serverFormData.join_url}
                    onChange={(e) => setServerFormData({ ...serverFormData, join_url: e.target.value })}
                    placeholder="steam://connect/192.168.1.100:27015"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="server-max-players">Max Players</Label>
                    <Input
                      id="server-max-players"
                      type="number"
                      value={serverFormData.max_players}
                      onChange={(e) => setServerFormData({ ...serverFormData, max_players: e.target.value })}
                      placeholder="20"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="server-current-players">Current Players</Label>
                    <Input
                      id="server-current-players"
                      type="number"
                      value={serverFormData.current_players}
                      onChange={(e) => setServerFormData({ ...serverFormData, current_players: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="server-version">Version</Label>
                    <Input
                      id="server-version"
                      type="text"
                      value={serverFormData.version}
                      onChange={(e) => setServerFormData({ ...serverFormData, version: e.target.value })}
                      placeholder="1.20.4"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="server-map">Map Name</Label>
                    <Input
                      id="server-map"
                      type="text"
                      value={serverFormData.map_name}
                      onChange={(e) => setServerFormData({ ...serverFormData, map_name: e.target.value })}
                      placeholder="de_dust2"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="server-status">Status</Label>
                    <Select
                      value={serverFormData.status}
                      onValueChange={(value) => setServerFormData({ ...serverFormData, status: value })}
                    >
                      <SelectTrigger id="server-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="offline">Offline</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="server-mods">Mods</Label>
                  <Input
                    id="server-mods"
                    type="text"
                    value={serverFormData.mods}
                    onChange={(e) => setServerFormData({ ...serverFormData, mods: e.target.value })}
                    placeholder="OptiFine, Fabric API"
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="server-password"
                      checked={serverFormData.password_protected}
                      onChange={(e) => setServerFormData({ ...serverFormData, password_protected: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="server-password" className="cursor-pointer">
                      Password Protected
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="server-active"
                      checked={serverFormData.is_active}
                      onChange={(e) => setServerFormData({ ...serverFormData, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="server-active" className="cursor-pointer">
                      Zichtbaar voor deelnemers
                    </Label>
                  </div>
                </div>

                {/* OBS-overlay integratie — voor CS 1.6 (en later andere GoldSrc-games)
                    haalt de backend elke 2s via RCON de status op en pusht die naar
                    /overlay/:identifier. Beide velden invullen en poller pakt 'm op. */}
                <div className="rounded border border-acid/30 bg-acid/5 p-3 space-y-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-acid">
                    OBS-overlay (CS 1.6 · RCON)
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="server-rcon" className="text-xs">RCON wachtwoord</Label>
                      <Input
                        id="server-rcon"
                        type="password"
                        value={serverFormData.rcon_password || ''}
                        onChange={(e) => setServerFormData({ ...serverFormData, rcon_password: e.target.value })}
                        placeholder="rcon_password uit server.cfg"
                        className="font-mono text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="server-overlay-id" className="text-xs">Overlay-identifier</Label>
                      <Input
                        id="server-overlay-id"
                        value={serverFormData.overlay_identifier || ''}
                        onChange={(e) => setServerFormData({ ...serverFormData, overlay_identifier: e.target.value })}
                        placeholder="bv. cs16-final-2026"
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                  {serverFormData.overlay_identifier && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">OBS browser-source URL:</span>
                      <code className="flex-1 px-2 py-1 rounded bg-background border border-border font-mono text-[11px] overflow-x-auto whitespace-nowrap">
                        {typeof window !== 'undefined' ? window.location.origin : ''}/overlay/{serverFormData.overlay_identifier}?variant=cs16
                      </code>
                      <Button size="sm" variant="outline" type="button"
                              onClick={() => {
                                const url = `${window.location.origin}/overlay/${serverFormData.overlay_identifier}?variant=cs16`
                                navigator.clipboard?.writeText(url).then(() => alert('URL gekopieerd'))
                              }}>
                        Kopieer
                      </Button>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setServerDialogOpen(false)
                      setServerError('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {editingServer ? 'Update Server' : 'Create Server'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Events Tab */}
        {/* Events- en Tournaments-tabs zijn verwijderd — waren pure
            navigate-stubs naar /events en /tournaments. Die sidebar-items
            zijn de canonieke plek voor die modules. */}

        {/* Music Tab - Spotify Integration */}
        <TabsContent value="music" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="h-5 w-5" />
                Spotify Integration
              </CardTitle>
              <CardDescription>
                Connect your Spotify account to enable collaborative playlist features for your LAN party
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {spotifyError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{spotifyError}</AlertDescription>
                </Alert>
              )}

              {spotifyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : !spotifySettings?.connected ? (
                // Not connected state
                <div className="space-y-4">
                  <div className="rounded-lg border border-dashed border-muted-foreground/25 p-8">
                    <div className="text-center mb-6">
                      <Music className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold mb-2">No Spotify Connection</h3>
                      <p className="text-sm text-muted-foreground">
                        Connect your Spotify account to enable collaborative playlist features. Users will be able to search for songs and add them to a shared playlist.
                      </p>
                    </div>

                    {!showManualAuth ? (
                      <div className="text-center">
                        <Button
                          onClick={handleGetSpotifyAuthUrl}
                          className="gap-2"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Connect Spotify Account
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">Step 1: Authorize Spotify</Label>
                          <p className="text-xs text-muted-foreground mb-2">
                            Click the link below to open Spotify authorization in a new tab:
                          </p>
                          <a
                            href={spotifyAuthUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Open Spotify Authorization
                          </a>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="callbackUrl" className="text-sm font-semibold">
                            Step 2: Paste Callback URL
                          </Label>
                          <p className="text-xs text-muted-foreground mb-2">
                            After clicking "Akkoord" on Spotify, copy the ENTIRE URL from your browser address bar and paste it here:
                          </p>
                          <Input
                            id="callbackUrl"
                            placeholder="http://127.0.0.1:3001/api/spotify/callback?code=..."
                            value={spotifyCallbackUrl}
                            onChange={(e) => setSpotifyCallbackUrl(e.target.value)}
                            disabled={connectingSpotify}
                            className="font-mono text-xs"
                          />
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={handleManualSpotifyAuth}
                            disabled={connectingSpotify || !spotifyCallbackUrl.trim()}
                            className="gap-2"
                          >
                            {connectingSpotify ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Connecting...
                              </>
                            ) : (
                              'Complete Connection'
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowManualAuth(false)
                              setSpotifyAuthUrl('')
                              setSpotifyCallbackUrl('')
                            }}
                            disabled={connectingSpotify}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <Card className="bg-muted/50">
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2 text-sm">How it works:</h4>
                      <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                        <li>Click "Connect Spotify Account" to start</li>
                        <li>Open the authorization link in a new tab</li>
                        <li>Log in to Spotify and click "Akkoord"</li>
                        <li>Copy the full URL from your browser</li>
                        <li>Paste it back here and click "Complete Connection"</li>
                      </ol>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                // Connected state
                <div className="space-y-4">
                  <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-green-500">Connected to Spotify</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            Logged in as: <span className="font-medium">{spotifySettings.username}</span>
                          </p>
                          {spotifySettings.playlistName && (
                            <p className="text-sm text-muted-foreground">
                              Active playlist: <span className="font-medium">{spotifySettings.playlistName}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDisconnectSpotify}
                        disabled={spotifyLoading}
                      >
                        Disconnect
                      </Button>
                    </div>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Playlist Configuration</CardTitle>
                      <CardDescription>
                        Set the Spotify playlist that users can add songs to. You can find the playlist ID in the Spotify URL.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleUpdatePlaylist} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="playlistId">Spotify Playlist ID</Label>
                          <Input
                            id="playlistId"
                            placeholder="e.g., 37i9dQZF1DXcBWIGoYBM5M"
                            value={spotifyPlaylistId}
                            onChange={(e) => setSpotifyPlaylistId(e.target.value)}
                            disabled={spotifyLoading}
                          />
                          <p className="text-xs text-muted-foreground">
                            Find this in your Spotify playlist URL: spotify.com/playlist/<strong>PLAYLIST_ID</strong>
                          </p>
                        </div>
                        <Button type="submit" disabled={spotifyLoading || !spotifyPlaylistId}>
                          {spotifyLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Saving...
                            </>
                          ) : (
                            'Save Playlist Configuration'
                          )}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>

                  <Card className="bg-muted/50">
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2 text-sm">How to get a Playlist ID:</h4>
                      <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                        <li>Open Spotify and go to the playlist you want to use</li>
                        <li>Click the "..." menu and select "Share" → "Copy link to playlist"</li>
                        <li>The URL will look like: https://open.spotify.com/playlist/<strong>PLAYLIST_ID</strong>?si=...</li>
                        <li>Copy just the PLAYLIST_ID part and paste it above</li>
                      </ol>
                      <p className="text-xs text-muted-foreground mt-3 italic">
                        Note: Make sure the playlist is collaborative or public so users can add songs to it!
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Moderation Tab */}
        <TabsContent value="moderation" className="space-y-6">
          <ModerationPanel />
        </TabsContent>

        {/* Helpdesk Groups Tab */}
        <TabsContent value="groups" className="space-y-6">
          <Card className="border-purple-500/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserCog className="h-5 w-5 text-purple-500" />
                  <CardTitle>Helpdesk Groups</CardTitle>
                </div>
                <Button onClick={handleCreateGroup} className="bg-purple-500 hover:bg-purple-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Group
                </Button>
              </div>
              <CardDescription>
                Manage helpdesk groups for ticket assignment and organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              {groupsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                </div>
              ) : groups.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <UserCog className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No groups created yet</p>
                  <p className="text-sm">Create your first group to organize your helpdesk team</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {groups.map((group) => (
                    <Card key={group.id} className="border-purple-500/20">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3 flex-1">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: group.color }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h3 className="font-semibold">{group.name}</h3>
                                <Badge variant="outline" className="text-xs">
                                  {group.member_count || 0} members
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {group.ticket_count || 0} tickets
                                </Badge>
                              </div>
                              {group.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {group.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const response = await helpdeskGroupsAPI.getGroup(group.id)
                                  setSelectedGroup(response.data.group)
                                  setAddMemberDialogOpen(true)
                                } catch (err) {
                                  console.error('Failed to load group details:', err)
                                }
                              }}
                              className="text-purple-500 hover:text-purple-600"
                            >
                              <Users className="h-4 w-4 mr-1" />
                              Members
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditGroup(group)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteGroup(group.id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create/Edit Group Dialog */}
          <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {selectedGroup ? 'Edit Group' : 'Create New Group'}
                </DialogTitle>
                <DialogDescription>
                  {selectedGroup
                    ? 'Update group information and settings'
                    : 'Create a new helpdesk group for organizing your team'
                  }
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSaveGroup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="group-name">Group Name</Label>
                  <Input
                    id="group-name"
                    value={groupFormData.name}
                    onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                    placeholder="e.g., Compo, Hardware, Software"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="group-description">Description (Optional)</Label>
                  <Textarea
                    id="group-description"
                    value={groupFormData.description}
                    onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                    placeholder="Brief description of this group's purpose"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="group-color">Color</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="group-color"
                      type="color"
                      value={groupFormData.color}
                      onChange={(e) => setGroupFormData({ ...groupFormData, color: e.target.value })}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={groupFormData.color}
                      onChange={(e) => setGroupFormData({ ...groupFormData, color: e.target.value })}
                      placeholder="#3b82f6"
                      className="flex-1"
                    />
                  </div>
                </div>
                {groupError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{groupError}</AlertDescription>
                  </Alert>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setGroupDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-purple-500 hover:bg-purple-600">
                    {selectedGroup ? 'Update Group' : 'Create Group'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Member Management Dialog */}
          <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Manage Group Members</DialogTitle>
                <DialogDescription>
                  {selectedGroup && `Managing members for "${selectedGroup.name}"`}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Current Members */}
                <div>
                  <h4 className="font-semibold mb-2">Current Members ({selectedGroup?.members?.length || 0})</h4>
                  {selectedGroup?.members?.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {selectedGroup.members.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <p className="font-medium">{member.username}</p>
                            <p className="text-xs text-muted-foreground">{member.email}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(selectedGroup.id, member.id)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No members yet</p>
                  )}
                </div>

                {/* Add Member */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Add Member</h4>
                  <div className="flex gap-2">
                    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select a user" />
                      </SelectTrigger>
                      <SelectContent>
                        {users
                          .filter(u => !selectedGroup?.members?.some(m => m.id === u.id))
                          .map((user) => (
                            <SelectItem key={user.id} value={user.id.toString()}>
                              {user.username} ({user.email})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAddMember}
                      disabled={!selectedUserId}
                      className="bg-purple-500 hover:bg-purple-600"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setAddMemberDialogOpen(false)
                  setSelectedGroup(null)
                  setSelectedUserId('')
                }}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-6">
          {/* Theme picker */}
          <Card className="border-neon-blue/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Palette className="h-5 w-5 text-neon-blue" />
                Uiterlijk — Thema
              </CardTitle>
              <CardDescription>Kies het kleurthema voor de hele site. Geldt direct voor alle gebruikers.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { id: 'neon-blue',      label: 'Neon Blue',      accent: '#00d4ff', bg: '#1a1a2e', desc: 'Cyberpunk blauw (standaard)' },
                  { id: 'retro-green',    label: 'Retro Green',    accent: '#00ff41', bg: '#050f05', desc: 'Matrix / terminal groen' },
                  { id: 'blood-red',      label: 'Blood Red',      accent: '#ff2a2a', bg: '#1a0808', desc: 'Agressief gaming rood' },
                  { id: 'cyberpunk-gold', label: 'Cyberpunk Gold', accent: '#f0dc32', bg: '#0f0f0a', desc: 'Goud / amber accenten' },
                ].map(theme => (
                  <button
                    key={theme.id}
                    onClick={async () => {
                      setSavingTheme(true)
                      try {
                        await settingsAPI.setTheme(theme.id)
                        setActiveTheme(theme.id)
                        applyTheme(theme.id)
                      } catch {}
                      finally { setSavingTheme(false) }
                    }}
                    disabled={savingTheme}
                    className={`relative rounded-lg border-2 p-3 text-left transition-all ${
                      activeTheme === theme.id
                        ? 'border-white/60 ring-2 ring-white/30'
                        : 'border-border hover:border-border/80'
                    }`}
                    style={{ background: theme.bg }}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="h-4 w-4 rounded-full" style={{ background: theme.accent, boxShadow: `0 0 8px ${theme.accent}` }} />
                      <span className="text-xs font-semibold text-white">{theme.label}</span>
                    </div>
                    <p className="text-[10px] text-white/50">{theme.desc}</p>
                    {activeTheme === theme.id && (
                      <div className="absolute top-2 right-2 h-2 w-2 rounded-full bg-white" />
                    )}
                  </button>
                ))}
              </div>
              {savingTheme && <p className="text-xs text-muted-foreground mt-2">Opslaan...</p>}
            </CardContent>
          </Card>

          <Card className="border-neon-purple/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Boxes className="h-5 w-5 text-neon-purple" />
                    Feature Modules
                  </CardTitle>
                  <CardDescription>
                    Enable or disable features across the platform
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {modulesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-neon-blue" />
                </div>
              ) : modules.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No modules found
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Group by category */}
                  {['events', 'content', 'community', 'social', 'tools'].map(category => {
                    const categoryModules = modules.filter(m => m.category === category)
                    if (categoryModules.length === 0) return null

                    return (
                      <div key={category} className="space-y-3">
                        <h3 className="text-lg font-semibold capitalize text-neon-blue">
                          {category}
                        </h3>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                          {categoryModules.map(module => (
                            <Card key={module.id} className={`border ${module.is_enabled ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className="font-semibold">{module.module_name}</p>
                                      {module.is_enabled ? (
                                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                          Enabled
                                        </Badge>
                                      ) : (
                                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                          Disabled
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      {module.description}
                                    </p>
                                  </div>
                                  {can(currentUser?.role, 'manage_modules') && (
                                    <Button
                                      size="sm"
                                      variant={module.is_enabled ? 'destructive' : 'default'}
                                      onClick={() => handleToggleModule(module.module_key)}
                                      className="ml-2"
                                    >
                                      {module.is_enabled ? (
                                        <ToggleRight className="h-4 w-4" />
                                      ) : (
                                        <ToggleLeft className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Globe className="h-5 w-5 text-cyan-400" />
                Services &amp; Netwerk
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Bekijk server IP-adressen en configureer externe services
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchServiceOverview}
              disabled={serviceLoading}
            >
              {serviceLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Vernieuwen
            </Button>
          </div>

          {!serviceOverview && !serviceLoading && (
            <div className="flex justify-center py-12">
              <Button onClick={fetchServiceOverview} className="bg-cyan-600 hover:bg-cyan-700">
                <Globe className="h-4 w-4 mr-2" />
                Laad service overzicht
              </Button>
            </div>
          )}

          {serviceLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
            </div>
          )}

          {serviceOverview && !serviceLoading && (
            <div className="space-y-6">
              {/* Server IP addresses */}
              <Card className="border-cyan-500/30 bg-gradient-to-br from-card to-cyan-500/5">
                <CardHeader>
                  <CardTitle className="text-cyan-400 flex items-center gap-2">
                    <Server className="h-5 w-5" />
                    LAN Server IP-adressen
                  </CardTitle>
                  <CardDescription>
                    Alle actieve netwerk interfaces van deze server
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {serviceOverview.server_ips?.length === 0 ? (
                    <p className="text-muted-foreground text-sm">Geen externe IP-adressen gevonden</p>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {serviceOverview.server_ips?.map((entry, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border/50"
                        >
                          <div>
                            <p className="text-sm font-mono font-semibold text-cyan-300">{entry.ip}</p>
                            <p className="text-xs text-muted-foreground">{entry.interface} — {entry.netmask}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() => handleCopyIp(entry.ip)}
                          >
                            {copiedIp === entry.ip ? (
                              <Check className="h-4 w-4 text-green-400" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Configurable service IPs */}
              <Card className="border-orange-500/30 bg-gradient-to-br from-card to-orange-500/5">
                <CardHeader>
                  <CardTitle className="text-orange-400 flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Externe Services
                  </CardTitle>
                  <CardDescription>
                    Wijzig IP-adressen of URLs van externe services zonder herstart
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {serviceConfigs.map(cfg => {
                      const status = serviceStatus[cfg.key]
                      const isChecking = serviceChecking[cfg.key]
                      const isSaving = serviceSaving[cfg.key]
                      const currentEdit = serviceEditValues[cfg.key] ?? cfg.value
                      const isDirty = currentEdit !== cfg.value

                      return (
                        <div key={cfg.key} className="p-4 rounded-lg bg-muted/30 border border-border/50 space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-sm">{cfg.display_name}</p>
                              {cfg.description && (
                                <p className="text-xs text-muted-foreground mt-0.5">{cfg.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {status && (
                                <Badge
                                  variant="outline"
                                  className={status.online
                                    ? 'border-green-500/50 text-green-400'
                                    : 'border-red-500/50 text-red-400'
                                  }
                                >
                                  {status.online ? (
                                    <><Wifi className="h-3 w-3 mr-1" /> Online {status.ms}ms</>
                                  ) : (
                                    <><WifiOff className="h-3 w-3 mr-1" /> Offline</>
                                  )}
                                </Badge>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCheckService(cfg.key)}
                                disabled={isChecking}
                                className="h-7 text-xs"
                              >
                                {isChecking ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Wifi className="h-3 w-3" />
                                )}
                                <span className="ml-1">Test</span>
                              </Button>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Input
                              className="font-mono text-sm h-8"
                              value={currentEdit}
                              onChange={e => setServiceEditValues(prev => ({ ...prev, [cfg.key]: e.target.value }))}
                              placeholder={cfg.value}
                            />
                            <Button
                              size="sm"
                              className="h-8 shrink-0"
                              disabled={!isDirty || isSaving}
                              onClick={() => handleSaveServiceConfig(cfg.key)}
                            >
                              {isSaving ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Save className="h-3 w-3" />
                              )}
                              <span className="ml-1">Opslaan</span>
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Huidig: <span className="font-mono text-foreground/70">{cfg.value}</span>
                            {cfg.updated_at && (
                              <span className="ml-2">— bijgewerkt {formatRelativeTime(cfg.updated_at)}</span>
                            )}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Card className={`border-${systemStatus.database === 'online' ? 'green' : 'red'}-500/30`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Database</p>
                    <div className="flex items-center space-x-2 mt-2">
                      {systemStatus.database === 'online' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : systemStatus.database === 'error' ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <Loader2 className="h-5 w-5 animate-spin text-yellow-500" />
                      )}
                      <span className={`text-lg font-bold ${
                        systemStatus.database === 'online' ? 'text-green-500' :
                        systemStatus.database === 'error' ? 'text-red-500' : 'text-yellow-500'
                      }`}>
                        {systemStatus.database === 'online' ? 'Online' :
                         systemStatus.database === 'error' ? 'Error' : 'Checking...'}
                      </span>
                    </div>
                  </div>
                  <Database className={`h-10 w-10 ${
                    systemStatus.database === 'online' ? 'text-green-500' :
                    systemStatus.database === 'error' ? 'text-red-500' : 'text-yellow-500'
                  } opacity-50`} />
                </div>
              </CardContent>
            </Card>

            <Card className={`border-${systemStatus.backend === 'online' ? 'green' : 'red'}-500/30`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Backend API</p>
                    <div className="flex items-center space-x-2 mt-2">
                      {systemStatus.backend === 'online' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : systemStatus.backend === 'error' ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <Loader2 className="h-5 w-5 animate-spin text-yellow-500" />
                      )}
                      <span className={`text-lg font-bold ${
                        systemStatus.backend === 'online' ? 'text-green-500' :
                        systemStatus.backend === 'error' ? 'text-red-500' : 'text-yellow-500'
                      }`}>
                        {systemStatus.backend === 'online' ? 'Online' :
                         systemStatus.backend === 'error' ? 'Error' : 'Checking...'}
                      </span>
                    </div>
                  </div>
                  <Server className={`h-10 w-10 ${
                    systemStatus.backend === 'online' ? 'text-green-500' :
                    systemStatus.backend === 'error' ? 'text-red-500' : 'text-yellow-500'
                  } opacity-50`} />
                </div>
              </CardContent>
            </Card>

            <Card className={`border-${systemStatus.storage === 'online' ? 'green' : 'red'}-500/30`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Storage</p>
                    <div className="flex items-center space-x-2 mt-2">
                      {systemStatus.storage === 'online' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : systemStatus.storage === 'error' ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <Loader2 className="h-5 w-5 animate-spin text-yellow-500" />
                      )}
                      <span className={`text-lg font-bold ${
                        systemStatus.storage === 'online' ? 'text-green-500' :
                        systemStatus.storage === 'error' ? 'text-red-500' : 'text-yellow-500'
                      }`}>
                        {systemStatus.storage === 'online' ? 'Online' :
                         systemStatus.storage === 'error' ? 'Error' : 'Checking...'}
                      </span>
                    </div>
                  </div>
                  <HardDrive className={`h-10 w-10 ${
                    systemStatus.storage === 'online' ? 'text-green-500' :
                    systemStatus.storage === 'error' ? 'text-red-500' : 'text-yellow-500'
                  } opacity-50`} />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-lime-500/30">
            <CardHeader>
              <CardTitle>System Information</CardTitle>
              <CardDescription>Current system status and metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-md bg-muted/30">
                  <div className="flex-1">
                    <p className="font-medium">NAS Opslag</p>
                    <p className="text-sm text-muted-foreground">
                      {formatFileSize(stats.nasUsed)} gebruikt — {formatFileSize(stats.nasFree)} vrij van {formatFileSize(stats.nasTotal)}
                    </p>
                    {stats.nasTotal > 0 && (
                      <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                        <div
                          className="bg-amber-500 h-1.5 rounded-full"
                          style={{ width: `${Math.min(100, (stats.nasUsed / stats.nasTotal) * 100).toFixed(1)}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-lg ml-4">
                    {stats.nasTotal > 0 ? `${((stats.nasUsed / stats.nasTotal) * 100).toFixed(1)}%` : '—'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-4 rounded-md bg-muted/30">
                  <div>
                    <p className="font-medium">Geüploade bestanden (DB)</p>
                    <p className="text-sm text-muted-foreground">
                      Via de Files sectie geüpload
                    </p>
                  </div>
                  <Badge variant="outline" className="text-lg">
                    {stats.totalFiles} ({formatFileSize(stats.storageUsed)})
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-4 rounded-md bg-muted/30">
                  <div>
                    <p className="font-medium">Active Users</p>
                    <p className="text-sm text-muted-foreground">
                      Registered user accounts
                    </p>
                  </div>
                  <Badge variant="outline" className="text-lg">
                    {stats.totalUsers}
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-4 rounded-md bg-muted/30">
                  <div>
                    <p className="font-medium">Content Items</p>
                    <p className="text-sm text-muted-foreground">
                      Geüploade bestanden, events en tournaments
                    </p>
                  </div>
                  <Badge variant="outline" className="text-lg">
                    {stats.totalFiles + stats.totalEvents + stats.totalTournaments}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dangerous Actions — superadmin only */}
          {can(currentUser?.role, 'delete_anything') && (
            <Card className="border-red-500/50 bg-gradient-to-br from-red-900/10 to-transparent">
              <CardHeader>
                <CardTitle className="text-red-500 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Dangerous Actions
                </CardTitle>
                <CardDescription>
                  These actions cannot be undone. Use with extreme caution.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-md bg-red-500/10 border border-red-500/30">
                    <div>
                      <p className="font-medium text-red-400">Reset All Player Stats</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Permanently deletes all player statistics, achievements, and tournament placements.
                        This will give all players a fresh start. This action cannot be undone!
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => setResetStatsDialogOpen(true)}
                      className="ml-4 bg-red-600 hover:bg-red-700"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset Stats
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-md bg-red-500/10 border border-red-500/30">
                    <div>
                      <p className="font-medium text-red-400">Delete All Tournaments</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Permanently deletes all tournaments, including all participants, matches, and scores.
                        This is useful for clearing test tournaments or starting fresh. This action cannot be undone!
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => setDeleteTournamentsDialogOpen(true)}
                      className="ml-4 bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete All
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* System Shutdown — superadmin only */}
          {can(currentUser?.role, 'delete_anything') && (
            <Card className="border-red-500/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-400">
                  <PowerOff className="h-5 w-5" />
                  Systeem Afsluiten
                </CardTitle>
                <CardDescription>
                  Sluit alle game servers, containers, TrueNAS en Proxmox gecontroleerd af.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 rounded-md bg-red-500/10 border border-red-500/20">
                  <div>
                    <p className="font-medium text-red-300">LAN Party beëindigen</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Stopt alle game servers → TrueNAS → containers → Proxmox host
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setShutdownDialogOpen(true)}
                    className="ml-4 bg-red-700 hover:bg-red-800 border border-red-500"
                  >
                    <PowerOff className="h-4 w-4 mr-2" />
                    Afsluiten
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        {/* Kaarten Tab */}
        <TabsContent value="clan" className="space-y-6">
          <ClanTab isSuperAdmin={can(currentUser?.role, 'manage_clan_mutate')} />
        </TabsContent>

      
        <TabsContent value="badges" className="space-y-6">
          <BadgesTab />
        </TabsContent>

      </Tabs>

      {/* Reset Stats Confirmation Dialog */}
      <Dialog open={resetStatsDialogOpen} onOpenChange={setResetStatsDialogOpen}>
        <DialogContent className="border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Reset All Player Stats
            </DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Warning: Irreversible Action</AlertTitle>
              <AlertDescription>
                This will permanently delete:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>All player statistics (wins, losses, points, streaks)</li>
                  <li>All earned achievements</li>
                  <li>All tournament placements</li>
                </ul>
                <p className="mt-2 font-semibold">
                  Every player will start from zero. Are you absolutely sure?
                </p>
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetStatsDialogOpen(false)}
              disabled={resettingStats}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetPlayerStats}
              disabled={resettingStats}
              className="bg-red-600 hover:bg-red-700"
            >
              {resettingStats ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Yes, Reset All Stats
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Tournaments Confirmation Dialog */}
      <Dialog open={deleteTournamentsDialogOpen} onOpenChange={setDeleteTournamentsDialogOpen}>
        <DialogContent className="border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Delete All Tournaments
            </DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Warning: Irreversible Action</AlertTitle>
              <AlertDescription>
                This will permanently delete:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>All tournaments (registration, in-progress, and completed)</li>
                  <li>All tournament participants and team registrations</li>
                  <li>All tournament matches and brackets</li>
                  <li>All deathmatch scores and placements</li>
                </ul>
                <p className="mt-2 font-semibold">
                  All tournament data will be lost forever. Are you absolutely sure?
                </p>
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTournamentsDialogOpen(false)}
              disabled={deletingTournaments}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAllTournaments}
              disabled={deletingTournaments}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingTournaments ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Yes, Delete All Tournaments
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shutdown Dialog */}
      <Dialog open={shutdownDialogOpen} onOpenChange={shuttingDown ? undefined : setShutdownDialogOpen}>
        <DialogContent className="border-red-500/40 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <PowerOff className="h-5 w-5" />
              Systeem Afsluiten
            </DialogTitle>
            <DialogDescription>
              Dit sluit alle game servers, TrueNAS, alle containers en de Proxmox host af.
              De server zal volledig uitgeschakeld worden.
            </DialogDescription>
          </DialogHeader>

          {shutdownProgress.length > 0 && (
            <div className="bg-black/50 rounded-md p-3 max-h-60 overflow-y-auto font-mono text-xs space-y-1">
              {shutdownProgress.map((entry, i) => (
                <div key={i} className={`flex gap-2 ${
                  entry.status === 'error' ? 'text-red-400' :
                  entry.status === 'done' ? 'text-green-400' :
                  entry.status === 'start' ? 'text-yellow-400' : 'text-gray-300'
                }`}>
                  <span className="text-gray-500">{entry.time}</span>
                  <span>{entry.message}</span>
                </div>
              ))}
              {shuttingDown && shutdownProgress.length > 0 &&
                shutdownProgress[shutdownProgress.length - 1]?.status !== 'done' && (
                <div className="flex gap-2 text-gray-400">
                  <Loader2 className="h-3 w-3 animate-spin mt-0.5" />
                  <span>Bezig...</span>
                </div>
              )}
            </div>
          )}

          {!shuttingDown && shutdownProgress.length === 0 && (
            <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-md border border-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
              <p className="text-sm text-red-300">
                Ben je zeker? De volledige server infrastructuur wordt afgesloten.
              </p>
            </div>
          )}

          <DialogFooter>
            {!shuttingDown && shutdownProgress.length === 0 ? (
              <>
                <Button variant="outline" onClick={() => setShutdownDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleShutdown}
                  className="bg-red-700 hover:bg-red-800"
                >
                  <PowerOff className="h-4 w-4 mr-2" />
                  Bevestig Afsluiten
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => { setShutdownDialogOpen(false); setShuttingDown(false); setShutdownProgress([]) }}
                disabled={shuttingDown && shutdownProgress[shutdownProgress.length - 1]?.status !== 'done'}
              >
                Sluiten
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/* Badges beheer — badge_types CRUD */
/* ── Nieuws-broadcast: stuur één notificatie naar iedereen ───────
   Bewust simpel gehouden: 1 titel + optioneel bericht + optionele
   link. Iedereen krijgt een toast + een rij in z'n bell-icoon. */
function NewsTabContent() {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [actionUrl, setActionUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState(null)

  const verzend = async () => {
    if (!title.trim()) { setFeedback({ type: 'err', text: 'Titel is verplicht' }); return }
    if (!window.confirm(`"${title.trim()}" naar alle users sturen?`)) return
    setBusy(true); setFeedback(null)
    try {
      const r = await adminAPI.broadcastNotification({
        title: title.trim(),
        message: message.trim() || null,
        action_url: actionUrl.trim() || null,
      })
      setFeedback({ type: 'ok', text: `Verstuurd naar ${r.data.count} users` })
      setTitle(''); setMessage(''); setActionUrl('')
    } catch (e) {
      setFeedback({ type: 'err', text: e.response?.data?.error || e.message })
    } finally { setBusy(false) }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Nieuws-bericht broadcasten</CardTitle>
        <p className="text-xs text-muted-foreground">
          Iedereen krijgt een toast + een rij in z'n notificatie-bel. Handig voor "show over 5min", "prijzenkraam open", "pizza is er".
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor="news-title" className="text-xs">Titel *</Label>
          <Input id="news-title" value={title} onChange={e => setTitle(e.target.value)}
                 placeholder="bv. Prijzenkraam gaat open om 22u!"
                 disabled={busy} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="news-message" className="text-xs">Bericht (optioneel)</Label>
          <textarea id="news-message" value={message} onChange={e => setMessage(e.target.value)}
                    placeholder="Extra info…"
                    disabled={busy} rows={3}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <Label htmlFor="news-url" className="text-xs">Link (optioneel)</Label>
          <Input id="news-url" value={actionUrl} onChange={e => setActionUrl(e.target.value)}
                 placeholder="bv. /prijzenkraam"
                 disabled={busy} className="mt-1" />
        </div>
        {feedback && (
          <div className={`text-sm ${feedback.type === 'ok' ? 'text-live' : 'text-signal'}`}>
            {feedback.text}
          </div>
        )}
        <Button onClick={verzend} disabled={busy || !title.trim()}>
          {busy ? 'Verzenden…' : 'Verzend naar iedereen'}
        </Button>
      </CardContent>
    </Card>
  )
}

/* Pterodactyl-resources per server — één card per server met 3 lijnen
   (opslag, geheugen, CPU). Bovenaan een node-totaal-summary. */

/* Admin-view voor bug/idee-bus — lijst met filter. Klik = detail-panel
   met alle comments + reply-form + status-knoppen. */
function FeedbackAdminTab() {
  const [reports, setReports] = useState([])
  const [filter, setFilter] = useState('open')
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState(null)

  const laad = async () => {
    setLoading(true)
    try {
      const r = await reportsAPI.list(filter ? { status: filter } : undefined)
      setReports(r.data.reports || [])
    } catch { setReports([]) }
    finally { setLoading(false) }
  }
  useEffect(() => { laad() }, [filter])

  const STATUSES = [
    ['open', 'Open'], ['in_progress', 'In behandeling'],
    ['done', 'Afgewerkt'], ['dismissed', 'Afgewezen'],
  ]

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="text-sm">Feedback-inbox ({reports.length})</CardTitle>
        <div className="flex gap-1">
          {[['','Alle'], ...STATUSES].map(([k,l]) => (
            <button key={k || 'all'} type="button"
                    onClick={() => setFilter(k)}
                    className={`px-2 py-1 text-[11px] uppercase tracking-widest rounded ${
                      filter === k ? 'bg-acid/10 text-acid border border-acid/40' : 'text-muted-foreground hover:text-foreground border border-transparent'
                    }`}>
              {l}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && !reports.length && <p className="text-sm text-muted-foreground">Laden…</p>}
        {!loading && !reports.length && <p className="text-sm text-muted-foreground">Geen reports in deze filter.</p>}
        {reports.map(r => (
          <FeedbackRowAdmin key={r.id} r={r} STATUSES={STATUSES}
                            expanded={openId === r.id}
                            onToggle={() => setOpenId(openId === r.id ? null : r.id)}
                            onChanged={laad} />
        ))}
      </CardContent>
    </Card>
  )
}

function FeedbackRowAdmin({ r, STATUSES, expanded, onToggle, onChanged }) {
  const [busy, setBusy] = useState(false)
  const [detail, setDetail] = useState(null)
  const [reply, setReply] = useState('')

  useEffect(() => {
    if (!expanded) return
    reportsAPI.detail(r.id).then(x => setDetail(x.data)).catch(() => setDetail(null))
  }, [expanded, r.id])

  const setStatus = async (status) => {
    setBusy(true)
    try {
      await reportsAPI.update(r.id, { status })
      onChanged()
      if (expanded) {
        const x = await reportsAPI.detail(r.id)
        setDetail(x.data)
      }
    } catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  const postReply = async (e) => {
    e.preventDefault()
    if (!reply.trim()) return
    setBusy(true)
    try {
      await reportsAPI.addComment(r.id, reply.trim())
      setReply('')
      const x = await reportsAPI.detail(r.id)
      setDetail(x.data)
    } catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  return (
    <div className="rounded-md border border-border/60 bg-card/50 p-3 space-y-2">
      <button type="button" onClick={onToggle} className="w-full text-left">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`pf-mono text-[10px] uppercase tracking-widest ${r.type === 'bug' ? 'text-signal' : 'text-acid'}`}>
            {r.type === 'bug' ? '🐛 Bug' : '💡 Idee'}
          </span>
          <span className="font-semibold text-sm">{r.title}</span>
          <span className="text-[10px] text-muted-foreground ml-auto">
            {r.display_name || r.username} · {new Date(r.created_at).toLocaleString('nl-BE', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
          </span>
        </div>
        {r.body && <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-1">{r.body}</p>}
      </button>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Status:</span>
        {STATUSES.map(([s, l]) => (
          <button key={s} type="button" disabled={busy}
                  onClick={() => setStatus(s)}
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-widest rounded border ${
                    r.status === s
                      ? 'bg-acid/10 text-acid border-acid/40'
                      : 'text-muted-foreground border-border hover:border-acid/40'
                  }`}>
            {l}
          </button>
        ))}
      </div>
      {expanded && (
        <div className="border-t border-border/40 pt-2 space-y-2">
          {detail?.attachments?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {detail.attachments.map(a => (
                <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer">
                  <img src={a.url} alt={a.filename} className="h-16 w-16 object-cover rounded border border-border" />
                </a>
              ))}
            </div>
          )}
          <div className="space-y-1">
            {(detail?.comments || []).map(c => (
              <div key={c.id} className={`rounded p-2 text-xs ${
                c.is_admin ? 'bg-acid/5 border border-acid/20' : 'bg-muted/30 border border-border/40'
              }`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-[11px]">{c.display_name || c.username}</span>
                  {c.is_admin && <span className="text-[9px] text-acid">Admin</span>}
                  <span className="text-[9px] text-muted-foreground ml-auto">
                    {new Date(c.created_at).toLocaleString('nl-BE', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                  </span>
                </div>
                <p className="whitespace-pre-wrap">{c.body}</p>
              </div>
            ))}
            {(!detail?.comments || !detail.comments.length) && (
              <p className="text-[11px] text-muted-foreground italic">Nog geen reacties.</p>
            )}
          </div>
          <form onSubmit={postReply} className="flex items-start gap-2">
            <Input value={reply} placeholder="Reageer als admin…"
                   onChange={e => setReply(e.target.value)}
                   disabled={busy} className="h-8 text-xs" />
            <Button type="submit" size="sm" disabled={busy || !reply.trim()}>
              Verzend
            </Button>
          </form>
        </div>
      )}
    </div>
  )
}

function PterodactylResourcesCard({ ptStorage, loading, onRefresh }) {
  const fmt = (bytes) => {
    if (bytes == null) return '–'
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
  }
  const barColor = (pct) => pct > 90 ? '#ef4444' : pct > 70 ? '#f59e0b' : '#22c55e'

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Server className="h-5 w-5 text-neon-blue" />
            <CardTitle className="text-base">Server-resources — Pterodactyl</CardTitle>
          </div>
          <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Vernieuwen
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && !ptStorage && <p className="text-sm text-muted-foreground">Laden…</p>}
        {!loading && !ptStorage && (
          <p className="text-sm text-muted-foreground">Klik op Vernieuwen om te laden.</p>
        )}
        {ptStorage && (() => {
          const nodeDiskLimit = (ptStorage.nodeDiskMb ?? 0) * 1024 * 1024
          const nodeMemLimit  = (ptStorage.nodeMemoryMb ?? 0) * 1024 * 1024
          const diskPct = nodeDiskLimit > 0 ? Math.min(100, (ptStorage.totalDiskBytes / nodeDiskLimit) * 100) : 0
          const memPct  = nodeMemLimit  > 0 ? Math.min(100, (ptStorage.totalMemoryBytes / nodeMemLimit) * 100) : 0
          return (
            <div className="space-y-4">
              {/* Node totaal — 2 balken naast elkaar */}
              <div className="p-3 bg-muted/30 rounded-lg grid gap-3 md:grid-cols-2">
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium">Node opslag</span>
                    <span className="font-mono">
                      <span style={{ color: barColor(diskPct) }}>{fmt(ptStorage.totalDiskBytes)}</span>
                      <span className="text-muted-foreground"> / {fmt(nodeDiskLimit)}</span>
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                         style={{ width: `${diskPct}%`, backgroundColor: barColor(diskPct) }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium">Node geheugen</span>
                    <span className="font-mono">
                      <span style={{ color: barColor(memPct) }}>{fmt(ptStorage.totalMemoryBytes)}</span>
                      <span className="text-muted-foreground"> / {fmt(nodeMemLimit)}</span>
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                         style={{ width: `${memPct}%`, backgroundColor: barColor(memPct) }} />
                  </div>
                </div>
              </div>

              {/* Per server — één card, 3 lijnen (opslag / mem / cpu) */}
              <div className="grid gap-3 md:grid-cols-2">
                {ptStorage.servers.map(s => (
                  <ServerResourceCard key={s.identifier} s={s} fmt={fmt} barColor={barColor} />
                ))}
              </div>
            </div>
          )
        })()}
      </CardContent>
    </Card>
  )
}

function ServerResourceCard({ s, fmt, barColor }) {
  const isRunning = s.current_state === 'running'
  const diskLimit = s.disk_limit_mb > 0 ? s.disk_limit_mb * 1024 * 1024 : null
  const memLimit  = s.memory_limit_mb > 0 ? s.memory_limit_mb * 1024 * 1024 : null
  const diskPct = diskLimit ? Math.min(100, ((s.disk_bytes ?? 0) / diskLimit) * 100) : null
  const memPct  = memLimit && isRunning ? Math.min(100, ((s.memory_bytes ?? 0) / memLimit) * 100) : null
  const cpuAbs = s.cpu_absolute ?? 0
  // cpu_limit=0 = onbeperkt; % is dan tegen 100 als schaal
  const cpuLimit = s.cpu_limit > 0 ? s.cpu_limit : 100
  const cpuPct = isRunning ? Math.min(100, (cpuAbs / cpuLimit) * 100) : null

  return (
    <div className="rounded-md border border-border/60 bg-card/50 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${isRunning ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
          <span className="font-semibold text-sm truncate">{s.name}</span>
        </div>
        <span className="pf-mono text-[10px] uppercase text-muted-foreground">{s.current_state || '—'}</span>
      </div>
      <ResBar label="Opslag" used={fmt(s.disk_bytes)} total={diskLimit ? fmt(diskLimit) : 'geen limiet'}
              pct={diskPct} barColor={barColor} />
      <ResBar label="Geheugen"
              used={isRunning ? fmt(s.memory_bytes) : 'offline'}
              total={memLimit ? fmt(memLimit) : (isRunning ? 'geen limiet' : '')}
              pct={memPct} barColor={barColor} />
      <ResBar label="CPU"
              used={isRunning ? `${cpuAbs.toFixed(1)} %` : 'offline'}
              total={s.cpu_limit > 0 ? `${s.cpu_limit} %` : (isRunning ? 'geen limiet' : '')}
              pct={cpuPct} barColor={barColor} />
    </div>
  )
}

function ResBar({ label, used, total, pct, barColor }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-0.5">
        <span className="text-muted-foreground font-medium w-16">{label}</span>
        <span className="font-mono">
          <span style={{ color: pct != null ? barColor(pct) : undefined }}>{used}</span>
          {total && <span className="text-muted-foreground"> / {total}</span>}
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        {pct != null && (
          <div className="h-full rounded-full transition-all"
               style={{ width: `${pct}%`, backgroundColor: barColor(pct) }} />
        )}
      </div>
    </div>
  )
}

function BadgesTab() {
  const [types, setTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState({ name: '', description: '', color: '#B4FF39', icon_url: '' })
  const [busy, setBusy] = useState(false)
  const load = async () => {
    try {
      const r = await badgeAPI.getAllTypes()
      setTypes(Array.isArray(r.data?.badges) ? r.data.badges : Array.isArray(r.data) ? r.data : [])
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])
  const create = async () => {
    if (!draft.name.trim()) return
    setBusy(true)
    try { await badgeAPI.createType?.(draft) ?? (await import('@/lib/api').then(m => m.default.post('/badges/types', draft)))
      setDraft({ name:'', description:'', color:'#B4FF39', icon_url:'' })
      await load()
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)) }
    finally { setBusy(false) }
  }
  const remove = async (id, name) => {
    if (!confirm(`Badge "${name}" verwijderen? Alle uitreikingen aan users worden ook verwijderd.`)) return
    setBusy(true)
    try { await badgeAPI.deleteType?.(id) ?? (await import('@/lib/api').then(m => m.default.delete(`/badges/types/${id}`)))
      await load()
    } finally { setBusy(false) }
  }
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Nieuwe badge</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Naam</Label><Input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="CS Winter Champion" /></div>
            <div><Label>Kleur</Label><Input type="color" value={draft.color} onChange={e => setDraft({ ...draft, color: e.target.value })} /></div>
            <div><Label>Icon URL (optioneel)</Label><Input value={draft.icon_url} onChange={e => setDraft({ ...draft, icon_url: e.target.value })} /></div>
          </div>
          <div><Label>Beschrijving</Label><Input value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} placeholder="Bijv. Winnaar CS Winter Cup 2026" /></div>
          <Button onClick={create} disabled={busy || !draft.name.trim()}>Maken</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Bestaande badges ({types.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {loading && <div className="text-sm text-muted-foreground">Laden…</div>}
          {!loading && types.length === 0 && <div className="text-sm text-muted-foreground">Nog geen badges.</div>}
          {types.map(b => (
            <div key={b.id} className="flex items-center gap-3 p-2 rounded border border-border">
              <span className="inline-block w-6 h-6 rounded-full border" style={{ background: b.color || '#3b82f6' }} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{b.name}</div>
                <div className="text-xs text-muted-foreground truncate">{b.description}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(b.id, b.name)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

/* Toggle voor zelfregistratie — standaard uit. Deelnemers komen uit de
   kassa-import, dus dit hoort alleen aan als je uitzonderlijk mensen van
   buitenaf laat inschrijven. */
function RegistratieToggle() {
  const [aan, setAan]     = useState(null)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout]   = useState(null)

  useEffect(() => {
    settingsAPI.getSecurity()
      .then(r => setAan(!!r.data?.registration_enabled))
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }, [])

  const wissel = async () => {
    setBezig(true); setFout(null)
    try {
      const nieuw = !aan
      await settingsAPI.setSecurity(nieuw)
      setAan(nieuw)
    } catch (e) { setFout(e.response?.data?.error ?? e.message) }
    finally { setBezig(false) }
  }

  return (
    <CardContent className="p-4 flex items-start gap-3 flex-wrap">
      <div className="flex-1 min-w-[15rem]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Zelfregistratie
        </div>
        <div className="font-semibold text-sm">
          {aan === null ? 'Laden…' : (aan ? 'AAN — iedereen kan zichzelf aanmaken' : 'UIT — alleen import of admin')}
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Standaard uit. Deelnemers komen uit de kassa-import. Zet alleen
          tijdelijk aan als je een externe deelnemer laat inschrijven.
        </p>
        {fout && <div className="text-xs text-signal mt-1">{fout}</div>}
      </div>
      <Button size="sm" variant={aan ? 'default' : 'outline'} disabled={bezig || aan === null} onClick={wissel}>
        {bezig ? 'Bezig…' : (aan ? 'Uitzetten' : 'Aanzetten')}
      </Button>
    </CardContent>
  )
}

/* Toggle voor publieke modus. Zet aan zodra je Funnel of een andere
   publieke tunnel start, en uit als je hem weer sluit. Dan verschijnt de
   banner, worden test-endpoints geweigerd en (later) verkort de JWT-tijd. */
function PubliekeModusToggle() {
  const [aan, setAan]     = useState(null)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout]   = useState(null)

  useEffect(() => {
    settingsAPI.getPublicMode()
      .then(r => setAan(!!r.data?.public_mode))
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }, [])

  const wissel = async () => {
    setBezig(true); setFout(null)
    try {
      const nieuw = !aan
      await settingsAPI.setPublicMode(nieuw)
      setAan(nieuw)
    } catch (e) { setFout(e.response?.data?.error ?? e.message) }
    finally { setBezig(false) }
  }

  return (
    <CardContent className="p-4 flex items-start gap-3 flex-wrap">
      <div className="flex-1 min-w-[15rem]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Publieke modus
        </div>
        <div className="font-semibold text-sm">
          {aan === null ? 'Laden…' : (aan ? 'AAN — banner zichtbaar, testfuncties uit' : 'UIT — LAN-modus')}
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Zet aan zolang je de site via Tailscale Funnel of een andere tunnel
          openzet. De banner verschijnt automatisch als het adres eindigt op
          <code className="mx-1 text-acid">.ts.net</code>, ook zonder deze toggle.
        </p>
        {fout && <div className="text-xs text-signal mt-1">{fout}</div>}
      </div>
      <Button size="sm" variant={aan ? 'default' : 'outline'} disabled={bezig || aan === null} onClick={wissel}>
        {bezig ? 'Bezig…' : (aan ? 'Uitzetten' : 'Aanzetten')}
      </Button>
    </CardContent>
  )
}

/* Tailscale Funnel bedienen. Één knop naast de publieke-modus toggle.
   Zet je Funnel uit, blijft de app op je LAN gewoon werken; de banner
   verdwijnt wanneer je ook de publieke-modus toggle uit zet. */
function FunnelToggle() {
  const [status, setStatus] = useState(null)   // { aan, url, target } of null
  const [bezig, setBezig]   = useState(false)
  const [fout, setFout]     = useState(null)
  const [oncontact, setOff] = useState(false)

  const laad = () => {
    adminAPI.getFunnelStatus()
      .then(r => {
        if (!r.data?.beschikbaar) { setOff(true); return }
        setStatus({ aan: r.data.aan, url: r.data.url, target: r.data.target })
      })
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }
  useEffect(() => { laad() }, [])

  const wissel = async () => {
    if (!status) return
    if (!status.aan && !window.confirm(
      'Funnel aanzetten? De server wordt bereikbaar vanaf het internet.'
    )) return
    setBezig(true); setFout(null)
    try {
      const r = status.aan ? await adminAPI.funnelOff() : await adminAPI.funnelOn()
      setStatus({ aan: r.data.aan, url: r.data.url, target: r.data.target })
    } catch (e) {
      setFout(e.response?.data?.error ?? e.message)
    } finally { setBezig(false) }
  }

  if (oncontact) {
    return (
      <CardContent className="p-4 flex items-start gap-3">
        <div className="flex-1 min-w-[15rem]">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Tailscale Funnel
          </div>
          <div className="font-semibold text-sm text-muted-foreground">Niet bedienbaar vanuit deze app</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            De tailscale-socket of -binary is niet gemount in de container.
            Zet aan/uit via de terminal met <code className="text-acid">tailscale funnel --https=443 off</code>.
          </p>
        </div>
      </CardContent>
    )
  }

  return (
    <CardContent className="p-4 flex items-start gap-3 flex-wrap">
      <div className="flex-1 min-w-[15rem]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Tailscale Funnel
        </div>
        <div className={`font-semibold text-sm ${status?.aan ? 'text-live' : ''}`}>
          {status === null ? 'Laden…'
            : status.aan ? `AAN — bereikbaar op internet`
            : 'UIT — alleen bereikbaar op tailnet en LAN'}
        </div>
        {status?.url && (
          <div className="font-mono text-[10px] text-muted-foreground mt-1 break-all">
            {status.url}
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Zet uit zodra je de site niet meer publiek nodig hebt. Zet ook
          "Publieke modus" hierboven uit — dan verdwijnt de banner en gaat
          de testhub weer aan.
        </p>
        {fout && <div className="text-xs text-signal mt-1">{fout}</div>}
      </div>
      <Button size="sm" variant={status?.aan ? 'default' : 'outline'}
              disabled={bezig || status === null} onClick={wissel}>
        {bezig ? 'Bezig…' : (status?.aan ? 'Uitzetten' : 'Aanzetten')}
      </Button>
    </CardContent>
  )
}
